import { Tag } from 'antd';
import apiFundProductsCall from 'apps/investcore/apiCalls/fund-products/list';
import apiEtfProductsCall from 'apps/investcore/apiCalls/etf-products/list';
import { companyColors } from 'apps/investment/constants';
import { DataTable } from 'common/data-table';
import BooleanIcons from 'common/ui/boolean-icons';
import ErrorMessage from 'common/ui/ErrorMessage';
import InfoCard from 'common/ui/info-card';
import { useFetchApiList } from 'hooks/api';
import { useEffect } from 'react';
import { FileTextIcon } from 'lucide-react';
import { formatDateTime } from 'utils/formatDate';
import { columnsGenerator as columnsGeneratorFundProducts } from '../fund-products/config';
import { columnsGenerator as columnsGeneratorETFProducts } from '../ETFProduct/config';

const ItemDetail = ({ item }) => {
  const {
    id,
    code,
    slug,
    name,
    company_name,
    description,
    account_opening_guideline,
    term_condition_url,
    open_account_contract_form_url,
    logo,
    is_econtract_required,
    created_at,
    updated_at,
    fmarket_id,
    nhsv_code,
    nhsv_fund_name,
    nhsv_request_esign_flow,
    ranking,
  } = item || {};

  const { data: fundProducts, error: errorFundProducts, load: loadFundProducts } = useFetchApiList(
    apiFundProductsCall,
    { resourceName: 'data' }
  );

  const { data: etfProducts, error: errorEtfProducts, load: loadEtfProducts } = useFetchApiList(
    apiEtfProductsCall,
    { resourceName: 'data' }
  );

  useEffect(() => {
    if (id) {
      loadFundProducts({ fund_manager: code });
      loadEtfProducts({ fund_manager_id: id });
    }
  }, [id, code]);

  return (
    <div className="space-y-4">
      <div className="space-y-4">
        {/* Basic Information */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <InfoCard label="Ranking" value={ranking} />
          <InfoCard
            label="Code"
            value={<Tag color={companyColors[code]}>{code}</Tag>}
          />
          <InfoCard label="Slug" value={slug} />
          <InfoCard label="Tên" value={name} />
          <InfoCard
            label="Logo"
            value={
              logo ? (
                <img
                  src={logo}
                  alt="Logo"
                  className="w-20 rounded object-contain"
                />
              ) : (
                '-'
              )
            }
          />
          <InfoCard
            label="Yêu cầu e-contract"
            value={<BooleanIcons value={is_econtract_required} />}
          />
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          <InfoCard label="Fmarket ID" value={fmarket_id} />
          <InfoCard label="NHSV Code" value={nhsv_code} />
          <InfoCard label="NHSV Fund Name" value={nhsv_fund_name} />
          <InfoCard label="NHSV Esign Flow" value={nhsv_request_esign_flow} />
          <InfoCard label="Tên công ty" value={company_name} />
        </div>

        {/* Documents and Guidelines Section */}
        <h3 className="text-base font-medium text-gray-800 pt-2 border-b flex items-center">
          <FileTextIcon className="mr-1" />
          Documents & Guidelines
        </h3>
        <div className="space-y-2">
          <InfoCard label="Mô tả" value={description} />
          <InfoCard label="Điều khoản" value={term_condition_url} />
          <InfoCard
            label="Biểu mẫu hợp đồng mở tài khoản"
            value={open_account_contract_form_url}
          />
        </div>

        {account_opening_guideline && (
          <InfoCard
            className="max-h-52 overflow-y-scroll"
            label="Hướng dẫn mở tài khoản"
            value={
              <div
                dangerouslySetInnerHTML={{
                  __html: account_opening_guideline,
                }}
              />
            }
          />
        )}
      </div>

      {/* Metadata Section */}
      <div className="bg-gray-50 -mx-6 -mb-6 p-6 rounded-b-lg mt-4 space-y-4">
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          <InfoCard label="Created At" value={formatDateTime(created_at)} />
          <InfoCard label="Updated At" value={formatDateTime(updated_at)} />
        </div>
      </div>

      <h3 className="text-base font-medium text-gray-800 pt-2 border-b flex items-center">
        Danh sách quỹ mở
      </h3>
      {!!errorFundProducts ? (
        <ErrorMessage errors={errorFundProducts} />
      ) : (
        <DataTable
          data={fundProducts || []}
          columns={columnsGeneratorFundProducts()}
        />
      )}

      <h3 className="text-base font-medium text-gray-800 pt-2 border-b flex items-center">
        Danh sách ETF
      </h3>
      {!!errorEtfProducts ? (
        <ErrorMessage errors={errorEtfProducts} />
      ) : (
        <DataTable
          data={etfProducts || []}
          columns={columnsGeneratorETFProducts()}
        />
      )}
    </div>
  );
};

export default ItemDetail;
